// Client for Tests: the test library, test cases, runs that compare models, batches, schedules and
// published model picks. Backend: api/core/routers/test_suites.py. Admins can pass customerId to
// work in another account (the platform account for platform routes); /admin/* is admin only.

import { useEffect, useMemo, useState } from 'react';
import { ApiError, TokenGetter } from './learningApi';

export type TargetType = 'function' | 'chain';
export type MaterialKind = 'text' | 'json' | 'image' | 'audio' | 'video' | 'pdf' | 'file';
export type Strictness = 'lenient' | 'normal' | 'strict';

export interface TestsAccess {
  enabled: boolean;
  is_admin: boolean;
  account_id: string;
  judge_function: { function_id: string; family_id: string; name: string; default_model: string | null } | null;
  limits: { max_materials: number; max_file_size: string };
  platform_account_id: string | null;
}

export interface Material {
  id: string;
  name: string;
  description: string | null;
  kind: MaterialKind;
  mime: string | null;
  tags: string[];
  media: { mime?: string; size?: number; width?: number; height?: number; duration_s?: number } | null;
  source: 'upload' | 'generated' | 'pack' | 'cc0';
  attribution: { title?: string; author?: string; license?: string; url?: string } | null;
  file_name: string | null;
  file_size: number | null;
  text: string | null;
  has_file: boolean;
  used_by: number;
  pack_key: string | null;
  created_at: string | null;
}

export interface MaterialPage {
  items: Material[];
  total: number;
  page: number;
  page_size: number;
  tags: string[];
  limit: number;
  count: number;
}

export interface CaseInputEntry {
  field: string;
  material_id?: string;
  text?: string;
  json?: unknown;
}

export interface InputPreview {
  field: string;
  type: MaterialKind | 'missing';
  text: string;
  material_id?: string;
}

export interface FieldCheck {
  path: string;
  op: 'equals' | 'contains' | 'between' | 'approx' | 'count_between';
  value?: unknown;
}

export interface Expected {
  description?: string;
  must_include?: string[];
  must_not_include?: string[];
  field_checks?: FieldCheck[];
  strictness?: Strictness;
  pass_threshold?: number;
}

export interface CaseLinkRef {
  link_id: string;
  target_type: TargetType;
  target_id: string;
  target_name: string | null;
  function_id?: string | null;
  enabled: boolean;
  has_expectation: boolean;
}

export interface TestCase {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  inputs: CaseInputEntry[];
  preview: InputPreview[];
  kinds: MaterialKind[];
  links: CaseLinkRef[];
  pack_key: string | null;
  created_at: string | null;
}

export interface TargetCase extends TestCase {
  link_id: string;
  case_id: string;
  enabled: boolean;
  expected: Expected;
  weight: number;
  critical: boolean;
  has_expectation: boolean;
}

export interface TargetOption {
  target_type: TargetType;
  target_id: string;
  function_id?: string;
  name: string;
  input_types?: string[];
  default_model?: string | null;
  cases: number;
}

export interface ChainStep {
  node_id: string;
  label: string;
  function_name: string;
  model: string;
}

export interface TargetInfo {
  target_type: TargetType;
  target_id: string;
  function_id?: string;
  name: string;
  description: string | null;
  default_model: string | null;
  allowed_models?: string[];
  input_types?: string[];
  has_schema?: boolean;
  platform_deadline_s?: number | null;
  allow_any_model?: boolean;
  transcribes_audio?: boolean;
  is_judge?: boolean;
  /** The last test picks applied to the function (what undo puts back). */
  applied?: {
    at: string;
    by: string | null;
    run_id: string | null;
    recommended: string | null;
    fallback_model: string | null;
    models: string[];
    previous_models: string[];
  } | null;
  steps?: ChainStep[];
}

export interface Pick {
  model: string;
  why: string;
  accuracy: number | null;
  pass_rate: number | null;
  success_rate: number | null;
  p50_s: number | null;
  p95_s: number | null;
  credits_per_run: number | null;
  runs: number;
}

export interface Picks {
  recommended: Pick | null;
  quality: Pick | null;
  budget: Pick | null;
  note: string | null;
  bar: { accuracy: number; pass_rate: number };
}

export interface Recommendation {
  action: 'keep' | 'switch';
  model: string | null;
  from_model: string | null;
  text: string;
  note: string | null;
  accuracy_diff?: number;
  cheaper?: number | null;
  p95_faster_s?: number | null;
}

export interface ModelStats {
  runs: number;
  skipped: number;
  skip_reasons: string[];
  accuracy: { score: number | null; graded: number; pass_rate: number | null; passes: number; critical_failures: number;
    worst_case: { case_id: string; score: number | null } | null };
  reliability: { success_rate: number | null; schema_valid_rate: number | null; errors: Record<string, number> };
  consistency: { cases_repeated: number; score_spread: number | null; agreement: number | null };
  speed: { p50_s: number | null; p95_s: number | null; max_s: number | null; within_deadline: number | null; within_time_limit: number | null };
  cost: { credits_total: number; credits_per_run: number | null; credits_per_pass: number | null; input_tokens: number | null; output_tokens: number | null };
  case_scores: Record<string, number | null>;
}

export interface Comparison {
  cases: number;
  diff: number;
  low: number;
  high: number;
  significant: boolean;
}

export interface RankRow {
  model: string;
  composite: number;
  accuracy: number | null;
  eligible: boolean;
  problems: string[];
  is_default: boolean;
}

export interface RunSummary {
  default_model: string | null;
  deadline_s: number | null;
  models: Record<string, ModelStats>;
  rankings: Record<'balanced' | 'accuracy' | 'cheapest', RankRow[]>;
  comparisons: Record<string, Comparison>;
  recommendation: Recommendation | null;
  picks: Picks;
  totals: { results: number; credits: number };
  check_failures?: Record<string, number>;
}

export interface Progress {
  total?: number;
  done?: number;
  failed?: number;
  skipped?: number;
  capped?: boolean;
}

export interface TestRun {
  id: string;
  customer_id: string;
  batch_id: string | null;
  target_type: TargetType;
  target_id: string;
  target_name: string | null;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  trigger: string;
  config: {
    models?: string[];
    repeats?: number;
    case_ids?: string[];
    chain_steps?: string[];
    judge_model?: string | null;
    /** The route's model group the models came from (`route_group`). */
    group?: { id: string; name: string };
  };
  progress: Progress;
  credits_used: number;
  credit_cap: number | null;
  error: string | null;
  created_by: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  default_model: string | null;
  default_accuracy: number | null;
  default_pass_rate: number | null;
  best_model: string | null;
  models_tested: number;
  summary?: RunSummary | null;
  /** Set when the run stopped at its cap or was cancelled with results left: what finishing would take. */
  remaining?: { results: number; credits: number };
}

export interface MatrixCell {
  score: number | null;
  ok: number;
  passed: number;
  errors: number;
  skipped: number;
  latency_ms: number | null;
}

export interface RunDetail {
  run: TestRun;
  cases: { id: string; name: string; preview: InputPreview[]; expected: Expected; critical: boolean }[];
  matrix: Record<string, Record<string, MatrixCell>>;
  regressions: { newly_failing: string[]; newly_passing: string[]; previous_run_id: string; model: string } | null;
  judge_function: { function_id: string; name: string } | null;
}

export interface JudgeVerdict {
  score: number | null;
  verdict?: 'pass' | 'partial' | 'fail';
  matched?: string[];
  missing?: string[];
  unexpected?: string[];
  reason?: string;
  model?: string;
  credits?: number;
  cached?: boolean;
  error?: string;
  run_ref?: string;
}

export interface TestResultRow {
  id: string;
  case_id: string | null;
  model: string;
  repeat_index: number;
  status: 'ok' | 'error' | 'skipped';
  output: unknown;
  error: string | null;
  model_used: string | null;
  latency_ms: number | null;
  credits: number;
  input_tokens: number | null;
  output_tokens: number | null;
  schema_valid: boolean | null;
  checks: Record<string, any> | null;
  judge: JudgeVerdict | null;
  score: number | null;
  passed: boolean | null;
  human_verdict: { verdict: 'pass' | 'fail'; note: string | null; by: string; at: string } | null;
  run_ref: string | null;
  created_at: string | null;
}

export interface Estimate {
  cases: number;
  graded_cases: number;
  models: string[];
  repeats: number;
  calls: number;
  credits: number;
  run_credits: number;
  judge_credits: number;
  per_model: Record<string, number>;
}

export interface BatchTargetRow {
  run_id: string;
  target_type: TargetType;
  target_id: string;
  target_name: string | null;
  status: TestRun['status'];
  progress: Progress;
  credits_used: number;
  default_model: string | null;
  default_accuracy: number | null;
  default_pass_rate: number | null;
  picks: Picks | null;
  recommendation: Recommendation | null;
  models_tested: number;
  error: string | null;
}

export interface Batch {
  id: string;
  customer_id: string;
  label: string;
  trigger: string;
  status: 'running' | 'completed';
  config: Record<string, any>;
  summary: { targets?: BatchTargetRow[]; runs?: number; completed?: number; finished?: number; switches?: number; credits?: number;
    skipped?: { target_type: string; target_id: string; reason: string }[] };
  schedule_id: string | null;
  created_by: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface Schedule {
  id: string;
  customer_id: string;
  name: string;
  scope: 'target' | 'platform';
  target_type: TargetType | null;
  target_id: string | null;
  cadence: 'weekly' | 'daily';
  day_of_week: number;
  hour_utc: number;
  models: string[];
  auto_new_models: boolean;
  repeats: number;
  enabled: boolean;
  email_digest: boolean;
  credit_cap: number | null;
  last_run_at: string | null;
  next_run_at: string | null;
  last_batch_id: string | null;
}

export type ScheduleInput = Omit<Schedule, 'id' | 'customer_id' | 'last_run_at' | 'next_run_at' | 'last_batch_id'>;

export interface TrendPoint {
  run_id: string;
  at: string | null;
  default_model: string | null;
  default_accuracy: number | null;
  default_pass_rate: number | null;
  best_model: string | null;
}

export interface TargetSummary {
  target_type: TargetType;
  target_id: string;
  function_id?: string | null;
  name: string | null;
  cases: number;
  graded_cases: number;
  last_run: TestRun | null;
  last_completed: TrendPoint | null;
  trend: TrendPoint[];
  schedule?: Schedule | null;
}

export interface PublishedModelRow {
  model: string;
  accuracy: number | null;
  pass_rate: number | null;
  success_rate: number | null;
  p50_s: number | null;
  p95_s: number | null;
  credits_per_run: number | null;
  runs: number | null;
}

export interface TestedRoute {
  function_name: string;
  platform_tool: string | null;
  kind: string | null;
  api_paths: string[];
  default_model: string | null;
  tested_at: string;
  cases: number;
  repeats: number;
  picks: { recommended: Pick | null; quality: Pick | null; budget: Pick | null };
  note: string | null;
  bar: { accuracy: number; pass_rate: number } | null;
  models: PublishedModelRow[];
}

export interface PlatformRouteTests extends TargetSummary {
  customer_id: string;
  function_id: string;
  family_id: string;
  platform_tool: string | null;
  kind: string | null;
  api_paths: string[];
  current_default: string | null;
  /** The model group `route_group` tests on: set on the route, or its kind's default (`auto`). */
  group: { id: string; name: string; auto: boolean; models: string[] } | null;
  tested: TestedRoute | null;
  recommendation: Recommendation | null;
  tested_run_id: string | null;
  published: boolean;
  published_by: string | null;
  applied: AppliedPicks | null;
}

/** The last time a route's picks were applied (kept so it can be undone). */
export interface AppliedPicks {
  at: string;
  by: string | null;
  run_id: string | null;
  fallback: ApplyFallback;
  recommended: string;
  fallback_model: string | null;
  previous_models: string[];
  models: string[];
}

export type ApplyFallback = 'budget' | 'quality' | 'none';

/** In a run's models: each platform route's own model group. */
export const ROUTE_GROUP = 'route_group';

export interface ModelGroup {
  id: string;
  name: string;
  description: string;
  models: string[];
}

export interface ModelGroups {
  groups: ModelGroup[];
  /** Admins only: family id -> group id set on that route. */
  routes: Record<string, string>;
  built_in: string[];
  /** Route kind -> group id; other kinds use `fallback_group`. */
  kind_groups: Record<string, string>;
  fallback_group: string;
  /** Built-in group id -> the catalog's suggested models. */
  suggested: Record<string, string[]>;
  /** False while the built-in groups are still the live suggestions. */
  saved: boolean;
}

export interface RouteRef {
  family_id: string;
  customer_id: string;
}

/** What applying picks does (or did) to one route. */
export interface ApplyRow extends RouteRef {
  name?: string;
  function_id?: string;
  current_models?: string[];
  new_models?: string[] | null;
  recommended?: string | null;
  fallback?: string | null;
  changed: boolean;
  applied?: boolean;
  notes: string[];
  error?: string;
}

export interface TestSettings {
  judge_model: string;
  concurrency: number;
  max_active_runs: number;
  pool_size: number;
  default_credit_cap: number;
  generator_account: string;
  generator_model: string;
  media_model: string;
  generator_count: number;
  /** Read only: the generator account and its functions once they exist. */
  generator?: {
    account_id: string | null;
    error: string | null;
    functions: { name: string; function_id: string; models: string[]; edited: boolean }[];
  };
}

export interface DraftInput {
  field: string;
  text?: string;
  json?: unknown;
  media?: { kind: string; prompt?: string; description: string };
}

export interface DraftCase {
  name: string;
  description: string | null;
  tags: string[];
  inputs: DraftInput[];
  expected: Expected;
  critical: boolean;
  /** Images that are made when the case is saved. */
  images: number;
  /** A file a person has to attach; the case is saved switched off. */
  needs_file: boolean;
}

export interface GenerateResult {
  cases: DraftCase[];
  problems: string[];
  credits: number;
  model: string;
  existing: number;
  images_allowed: boolean;
  /** Admins only: the function that wrote the drafts. */
  generator?: { account_id: string; function_id: string; name: string };
}

export const DEFAULT_BATCH_REPEATS = 3;
export const ACTIVE_STATUSES: TestRun['status'][] = ['queued', 'running'];

type Params = Record<string, string | number | boolean | null | undefined>;

function query(params: Params): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
  } catch {
    return res.statusText;
  }
}

export function testsApi(getToken: TokenGetter, customerId?: string | null) {
  const withCustomer = (path: string, params: Params = {}) => `/api/v2/tests${path}${query({ ...params, customer_id: customerId })}`;

  async function send(path: string, init: { method?: string; body?: unknown; form?: FormData } = {}, params: Params = {}): Promise<Response> {
    const token = await getToken();
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    let body: BodyInit | undefined;
    if (init.form) body = init.form;
    else if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(init.body);
    }
    const res = await fetch(withCustomer(path, params), { method: init.method || (body ? 'POST' : 'GET'), headers, body });
    if (!res.ok) throw new ApiError(await errorMessage(res), res.status);
    return res;
  }

  const get = async <T,>(path: string, params: Params = {}): Promise<T> => (await send(path, {}, params)).json();
  const post = async <T,>(path: string, body?: unknown): Promise<T> => (await send(path, { method: 'POST', body: body ?? {} })).json();
  const put = async <T,>(path: string, body: unknown): Promise<T> => (await send(path, { method: 'PUT', body })).json();
  const patch = async <T,>(path: string, body: unknown): Promise<T> => (await send(path, { method: 'PATCH', body })).json();
  const del = async <T,>(path: string): Promise<T> => (await send(path, { method: 'DELETE' })).json();

  async function download(path: string, params: Params, fallbackName: string): Promise<void> {
    const res = await send(path, {}, params);
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1] || fallbackName;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return {
    access: () => get<TestsAccess>('/access'),

    materials: (params: { search?: string; tag?: string; kind?: string; page?: number; page_size?: number } = {}) =>
      get<MaterialPage>('/materials', params),
    uploadMaterials: async (files: File[], tags: string[], description?: string) => {
      const form = new FormData();
      files.forEach(f => form.append('files', f));
      if (tags.length) form.append('tags', tags.join(','));
      if (description) form.append('description', description);
      return (await send('/materials/upload', { method: 'POST', form })).json() as Promise<{
        created: Material[]; already_in_library: Material[]; failed: { file: string; error: string }[] }>;
    },
    createTextMaterial: (body: { name: string; text?: string; json?: unknown; description?: string; tags?: string[] }) =>
      post<Material>('/materials/text', body),
    updateMaterial: (id: string, body: { name?: string; description?: string; tags?: string[] }) => patch<Material>(`/materials/${id}`, body),
    deleteMaterial: (id: string) => del<{ deleted: boolean; used_by: number }>(`/materials/${id}`),
    materialBlob: async (id: string): Promise<Blob> => (await send(`/materials/${id}/file`)).blob(),

    cases: (params: { search?: string; tag?: string; page?: number; page_size?: number } = {}) =>
      get<{ items: TestCase[]; total: number; page: number; page_size: number; tags: string[] }>('/cases', params),
    createCase: (body: { name: string; description?: string; tags?: string[]; inputs: CaseInputEntry[] }) => post<TestCase>('/cases', body),
    updateCase: (id: string, body: { name?: string; description?: string; tags?: string[]; inputs?: CaseInputEntry[] }) =>
      patch<TestCase>(`/cases/${id}`, body),
    deleteCase: (id: string) => del<{ deleted: boolean }>(`/cases/${id}`),
    linkCases: (caseIds: string[], targetType: TargetType, targetId: string, expected?: Expected) =>
      post<{ linked: number; re_enabled: number; not_found: number; target_id: string }>('/cases/link',
        { case_ids: caseIds, target_type: targetType, target_id: targetId, expected }),
    unlinkCases: (caseIds: string[], targetType: TargetType, targetId: string) =>
      post<{ disabled: number }>('/cases/unlink', { case_ids: caseIds, target_type: targetType, target_id: targetId }),
    caseFromTester: (body: { function_id: string; name: string; input?: unknown; input_type?: string; message?: string;
      material_ids?: string[]; output?: unknown; use_output_as_expected?: boolean; tags?: string[] }) =>
      post<{ case: TestCase; link_id: string; expected: Expected; target_id: string }>('/cases/from-tester', body),

    targets: (search?: string) => get<{ functions: TargetOption[]; chains: TargetOption[] }>('/targets', { search }),
    target: (type: TargetType, id: string) =>
      get<{ target: TargetInfo; cases: TargetCase[]; runs: TestRun[]; schedule: Schedule | null }>(`/targets/${type}/${id}`),
    updateLink: (type: TargetType, id: string, linkId: string, body: { expected?: Expected; enabled?: boolean; weight?: number; critical?: boolean }) =>
      put<{ link_id: string; expected: Expected; enabled: boolean; weight: number; critical: boolean }>(`/targets/${type}/${id}/links/${linkId}`, body),
    deleteLink: (type: TargetType, id: string, linkId: string) => del<{ deleted: boolean }>(`/targets/${type}/${id}/links/${linkId}`),
    suggestExpected: (type: TargetType, id: string, linkId: string) =>
      post<{ expected: Expected; output: unknown; model: string; credits: number }>(`/targets/${type}/${id}/links/${linkId}/suggest`),

    estimate: (targetType: TargetType, targetId: string, config: Record<string, unknown>) =>
      post<Estimate>('/runs/estimate', { target_type: targetType, target_id: targetId, config }),
    startRun: (targetType: TargetType, targetId: string, config: Record<string, unknown>, creditCap?: number | null) =>
      post<TestRun>('/runs', { target_type: targetType, target_id: targetId, config, credit_cap: creditCap || undefined }),
    runs: (params: { target_type?: TargetType; target_id?: string; batch_id?: string; limit?: number } = {}) =>
      get<{ runs: TestRun[] }>('/runs', params),
    run: (id: string) => get<RunDetail>(`/runs/${id}`),
    results: (id: string, params: { model?: string; case_id?: string; status?: string; page?: number; page_size?: number } = {}) =>
      get<{ results: TestResultRow[]; total: number; page: number; page_size: number }>(`/runs/${id}/results`, params),
    cancelRun: (id: string) => post<TestRun>(`/runs/${id}/cancel`),
    continueRun: (id: string, creditCap?: number | null) => post<TestRun>(`/runs/${id}/continue`, creditCap ? { credit_cap: creditCap } : {}),
    setVerdict: (runId: string, resultId: string, verdict: 'pass' | 'fail' | null, note?: string) =>
      put<{ result: TestResultRow; run: TestRun }>(`/runs/${runId}/results/${resultId}/verdict`, { verdict, note }),
    exportRun: (id: string, format: 'csv' | 'json') => download(`/runs/${id}/export`, { format }, `test-run.${format}`),
    makeDefault: (runId: string, model: string) =>
      post<{ changed: boolean; function_id: string; version?: number; previous_default?: string | null; default_model: string }>(
        `/runs/${runId}/make-default`, { model }),

    startBatch: (targets: { target_type: TargetType; target_id: string }[], config: Record<string, unknown>, label?: string, creditCap?: number | null) =>
      post<Batch>('/batches', { targets, config, label, credit_cap: creditCap || undefined }),
    batches: (limit = 30) => get<{ batches: Batch[] }>('/batches', { limit }),
    batch: (id: string) => get<Batch>(`/batches/${id}`),
    cancelBatch: (id: string) => post<Batch>(`/batches/${id}/cancel`),
    continueBatch: (id: string) => post<Batch>(`/batches/${id}/continue`),

    schedules: () => get<{ schedules: Schedule[] }>('/schedules'),
    createSchedule: (body: ScheduleInput) => post<Schedule>('/schedules', body),
    updateSchedule: (id: string, body: ScheduleInput) => put<Schedule>(`/schedules/${id}`, body),
    deleteSchedule: (id: string) => del<{ deleted: boolean }>(`/schedules/${id}`),
    runSchedule: (id: string) => post<Batch>(`/schedules/${id}/run`),

    overview: () => get<{ targets: TargetSummary[]; materials: number; cases: number }>('/overview'),

    platformResults: () => get<{ routes: PlatformRouteTests[]; batches: Batch[]; default_repeats: number }>('/admin/platform-results'),
    platformEstimate: (targets: { function_id: string; customer_id?: string }[], models: string[], repeats: number) =>
      post<{ targets: { name: string; cases: number; calls: number; credits: number; models: string[]; group: string | null }[];
        skipped: { name: string; reason: string }[];
        credits: number; calls: number }>('/admin/platform-batch', { targets, models, repeats, estimate_only: true }),
    platformBatch: (targets: { function_id: string; customer_id?: string }[], models: string[], repeats: number, creditCap?: number | null) =>
      post<{ batches: Batch[] }>('/admin/platform-batch', { targets, models, repeats, credit_cap: creditCap || undefined }),
    setPublished: (familyId: string, published: boolean) => put<{ published: boolean; by: string; at: string }>(`/admin/publish/${familyId}`, { published }),
    applyPicks: (routes: RouteRef[], fallback: ApplyFallback, dryRun: boolean) =>
      post<{ routes: ApplyRow[]; applied: number }>('/admin/apply-picks', { routes, fallback, dry_run: dryRun }),
    undoApplyPicks: (route: RouteRef) => post<{ changed: boolean; models: string[] }>('/admin/apply-picks/undo', route),
    applyTargetPicks: (targetId: string, fallback: ApplyFallback, dryRun: boolean, runId?: string | null) =>
      post<{ routes: ApplyRow[]; applied: number }>(`/targets/function/${targetId}/apply-picks`, {
        fallback,
        dry_run: dryRun,
        run_id: runId || undefined,
      }),
    undoTargetPicks: (targetId: string) => post<{ changed: boolean; models: string[] }>(`/targets/function/${targetId}/apply-picks/undo`),
    modelGroups: () => get<ModelGroups>('/model-groups'),
    saveModelGroups: (groups: (Omit<ModelGroup, 'id'> & { id?: string })[]) => put<ModelGroups>('/admin/model-groups', { groups }),
    setRouteGroup: (familyId: string, groupId: string | null) =>
      put<ModelGroups>('/admin/model-groups/routes', { family_id: familyId, group_id: groupId }),
    settings: () => get<TestSettings>('/admin/settings'),
    saveSettings: (body: Partial<TestSettings>) => put<TestSettings>('/admin/settings', body),
    generateCases: (body: {
      target_type: TargetType;
      target_id: string;
      count?: number;
      guidance?: string;
      avoid_existing?: boolean;
      images?: boolean;
      avoid?: DraftCase[];
    }) => post<GenerateResult>('/generate-cases', body),
    saveGeneratedCases: (body: { target_type: TargetType; target_id: string; cases: DraftCase[]; images: boolean }) =>
      post<{ saved: number; switched_off: number; images: number; credits: number; problems: string[] }>('/generate-cases/save', body),
    installPack: (pack = 'platform_v1') =>
      post<{ materials: number; cases: number; links: number; skipped_links: string[]; already_installed: number }>('/admin/pack/install', { pack }),
  };
}

export type TestsApi = ReturnType<typeof testsApi>;

// Every page mounts its own Layout, which asks for access; remember answers for a few minutes.
const ACCESS_TTL_MS = 5 * 60 * 1000;
const accessCache = new Map<string, { at: number; value: Promise<TestsAccess> }>();

// Keyed by the signed-in token too, so a different login in the same tab asks again.
async function cachedAccess(api: TestsApi, getToken: TokenGetter, customerId: string): Promise<TestsAccess> {
  const token = (await getToken()) || '';
  const key = `${customerId}|${token.slice(-24)}`;
  const hit = accessCache.get(key);
  if (hit && Date.now() - hit.at < ACCESS_TTL_MS) return hit.value;
  const value = api.access();
  value.catch(() => accessCache.delete(key));
  accessCache.set(key, { at: Date.now(), value });
  return value;
}

/** The Tests API for an account (admins can pass another account) and whether Tests are on there. */
export function useTestsAccess(getToken: TokenGetter, customerId?: string | null) {
  const api = useMemo(() => testsApi(getToken, customerId), [getToken, customerId]);
  const [access, setAccess] = useState<TestsAccess | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    cachedAccess(api, getToken, customerId || '')
      .then(a => !cancelled && setAccess(a))
      .catch(() => !cancelled && setAccess(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, getToken, customerId]);
  return { api, access, enabled: !!access?.enabled, loading };
}

export const fmtScore = (v: number | null | undefined) => (v === null || v === undefined ? '–' : v.toFixed(0));
export const fmtPctValue = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${v.toFixed(0)}%`);
export const fmtSeconds = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${v.toFixed(1)}s`);
export const fmtCredits = (v: number | null | undefined) =>
  v === null || v === undefined ? '–' : v < 0.01 ? v.toFixed(4) : v < 1 ? v.toFixed(3) : v.toFixed(2);

export const scoreColor = (v: number | null | undefined, bar = 85) =>
  v === null || v === undefined ? 'text.secondary' : v >= bar ? 'success.main' : v >= 70 ? 'warning.main' : 'error.main';

export const runIsActive = (r: { status: TestRun['status'] } | null | undefined) => !!r && ACTIVE_STATUSES.includes(r.status);
